
import { DataUiBlueprints, SimplifiedRelationType } from '@gid/models';
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { BIconPlus, BIconQuestionCircle } from 'bootstrap-vue';
import { CrudList } from '@gid/vue-common/store/shared/crud-list.module';

interface RegexPart {
  value: string;
  special: boolean;
}

interface RegexTransformer {
  column: string;
  findRegex: string;
  replaceWith: string;
}

@Component({
  name: 'RelationCloneConfig',
  components: {
    BIconPlus,
    BIconQuestionCircle,
  },
})
export default class RelationCloneConfig<Model> extends Vue {
  @Prop({ required: true, type: Function }) modelConstructor!: {
    new (...args: any[]): Model;
  };
  @Prop({ type: Function }) parentModelConstructor!: {
    new (...args: any[]): Model;
  };
  @Prop({ type: Object }) readonly store?: CrudList<any>;

  relations: any = [];
  transformers: RegexTransformer[] = [];
  currentStringColumn: string | null = null;

  @Watch('relations', { deep: true })
  watchRelations() {
    this.emitInput();
  }

  @Watch('transformers', { deep: true })
  watchTransformers() {
    this.emitInput();
  }

  get currentName() {
    return this.modelConstructor.name;
  }

  get stringColumns() {
    const columns = DataUiBlueprints.get(this.modelConstructor).groups?.table;
    if (!columns) {
      return [];
    }
    return Object.values(columns)
      .filter(
        (c) => !c.isRelation && !c.isPrimary && c.columnType.type === 'text',
      )
      .map((c) => c.propertyName);
  }

  get relationColumns() {
    const columns = DataUiBlueprints.get(this.modelConstructor).groups?.table;
    if (!columns) {
      return [];
    }
    return Object.values(columns)
      .filter(
        (c) =>
          c.isRelation &&
          c.relationType !== SimplifiedRelationType.MANY_TO_MANY &&
          c.relationEntity !== this.parentModelConstructor,
      )
      .map((c) => ({
        property: c.propertyName,
        name:
          typeof c.relationEntity === 'string'
            ? c.relationEntity
            : c.relationEntity.name,
        model: c.relationEntity,
        enabled: false,
        relationsEnabled: false,
        allowCloning: c.allowCloning,
        config: {},
      }));
  }

  get previewItem() {
    return (
      this.store?.currentPageItems?.[0] ?? this.store?.allItems?.[0] ?? null
    );
  }

  created() {
    this.relations = this.relationColumns;
  }

  addTransformer() {
    if (!this.currentStringColumn) {
      return;
    }

    this.transformers.push({
      column: this.currentStringColumn,
      findRegex: '',
      replaceWith: '',
    });
  }

  hasPreviewSource(transformer: RegexTransformer) {
    return (
      this.previewItem !== null &&
      this.previewItem !== undefined &&
      transformer.column in this.previewItem
    );
  }

  previewSource(transformer: RegexTransformer) {
    const value = this.previewItem?.[transformer.column];
    return value === null || value === undefined ? '' : String(value);
  }

  previewError(transformer: RegexTransformer) {
    if (!transformer.findRegex) {
      return null;
    }

    try {
      new RegExp(transformer.findRegex);
      return null;
    } catch (error: any) {
      return error.message;
    }
  }

  previewResult(transformer: RegexTransformer) {
    const source = this.previewSource(transformer);
    if (!transformer.findRegex) {
      return source;
    }

    try {
      return source.replace(
        new RegExp(transformer.findRegex),
        transformer.replaceWith,
      );
    } catch (error) {
      return source;
    }
  }

  regexParts(regex: string): RegexPart[] {
    const specialChars = new Set([
      '^',
      '$',
      '.',
      '*',
      '+',
      '?',
      '(',
      ')',
      '[',
      ']',
      '{',
      '}',
      '|',
    ]);
    const parts: RegexPart[] = [];

    for (let index = 0; index < regex.length; index++) {
      const value = regex[index];
      const escaped = value === '\\' && index + 1 < regex.length;
      const partValue = escaped ? regex.slice(index, index + 2) : value;

      parts.push({
        value: partValue,
        special: escaped || specialChars.has(value),
      });

      if (escaped) {
        index++;
      }
    }

    return parts;
  }

  visibleText(value: string) {
    return value
      .replace(/\n/g, '\\n')
      .replace(/\r/g, '\\r')
      .replace(/\t/g, '\\t')
      .replace(/ /g, '·');
  }

  emitInput() {
    this.$emit('input', {
      transformers: this.transformers,
      relations: this.relations
        .filter((r) => r.enabled)
        .map((r) => ({
          property: r.property,
          config: r.config,
        })),
    });
  }
}
